import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { stores as storesApi } from '../lib/api';
import StoreCard from '../components/StoreCard';
import { Search, MapPin } from 'lucide-react';

const allCats = ['all', 'kirana', 'vegetables', 'fancy', 'cloth', 'dairy', 'bakery', 'hotels', 'medical', 'electronics', 'electrical', 'mobiles', 'books', 'artifacts', 'jewellery'];

export default function Stores() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = sp.get('category') || 'all';
  const q = sp.get('q') || '';
  const village = sp.get('village') || '';
  const pincode = sp.get('pincode') || '';

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category && category !== 'all') params.category = category;
    if (q) params.q = q;
    if (village) params.village = village;
    if (pincode) params.pincode = pincode;
    storesApi.list(params).then(setItems).finally(() => setLoading(false));
  }, [category, q, village, pincode]);

  const updateParam = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val && val !== 'all') next.set(key, val); else next.delete(key);
    setSp(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <h1 className="font-heading font-black text-3xl md:text-4xl text-ink">All stores in your area</h1>
      <p className="text-ink/70 mt-1">Filter by PIN code, village or category to find what you need.</p>

      <div className="mt-6 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="flex items-center gap-2 rounded-full border border-border bg-white pl-4 pr-2 py-1.5 flex-1 max-w-lg">
          <Search className="w-4 h-4 text-muted-foreground"/>
          <input data-testid="stores-search-input" defaultValue={q} onKeyDown={(e) => { if (e.key === 'Enter') updateParam('q', e.currentTarget.value); }} placeholder="Search stores…" className="flex-1 outline-none text-sm bg-transparent py-1.5"/>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-[#0D47A1]/30 bg-[#E3F2FD]/40 pl-3 pr-1 py-1 w-full md:w-56">
          <MapPin className="w-4 h-4 text-[#0D47A1]"/>
          <input
            data-testid="stores-pincode-input"
            defaultValue={pincode}
            placeholder="6-digit PIN"
            inputMode="numeric"
            maxLength={6}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const v = e.currentTarget.value.replace(/\D/g, '');
                if (!v || /^\d{6}$/.test(v)) updateParam('pincode', v);
              }
            }}
            onChange={e => { e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 6); }}
            className="flex-1 outline-none text-sm bg-transparent py-1.5 font-mono tracking-wider"
          />
        </div>
        <input data-testid="stores-village-input" defaultValue={village} placeholder="Village / town" onKeyDown={(e) => { if (e.key === 'Enter') updateParam('village', e.currentTarget.value); }} className="rounded-full border border-border bg-white px-4 py-2 text-sm w-full md:w-56"/>
      </div>

      {pincode && (
        <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold rounded-full bg-[#0D47A1] text-white px-3 py-1">
          <MapPin className="w-3.5 h-3.5"/> PIN {pincode}
          <button data-testid="clear-pincode-filter" onClick={() => updateParam('pincode', '')} className="ml-1 opacity-80 hover:opacity-100" aria-label="Clear PIN filter">×</button>
        </div>
      )}

      <div className="mt-4 flex gap-2 overflow-x-auto scroll-hide pb-2">
        {allCats.map(c => (
          <button key={c} data-testid={`filter-${c}`} onClick={() => updateParam('category', c)}
            className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider border transition ${category===c?'bg-ink text-white border-ink':'bg-white border-border hover:border-[#E67E22] hover:text-[#D35400]'}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="mt-7 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? <div className="text-ink/60">Loading…</div> : items.length === 0 ? (
          <div data-testid="no-stores" className="col-span-full text-center py-16 text-ink/60">No stores found. Try a different filter.</div>
        ) : items.map(s => <StoreCard key={s.id} store={s}/>)}
      </div>
    </div>
  );
}
